import React, { PureComponent } from 'react';
import { Button } from 'reactstrap'; // Assuming reactstrap is used like in GoogleReviewForm
import { I } from '../../../common/v5/config';
import {
  TextInputRow,
  ReadOnlyTextField, // If needed for displaying status
  CustomInputRow,
} from '../../../reactcomponents/Form';
import {
  LabelToggleSwitch as ToggleSwitch, // If a similar toggle is needed for Lazada
} from '../../../components/v5/ToggleSwitch';
import { Spinner } from '../../../reactcomponents/Spinner';
import { INPUT_EMPTY_WARNING, FORM_SUBMIT_EMPTY_WARNING } from "../../../common/v5/constants";
import update from 'immutability-helper'; // Used in GoogleReview, might be useful
import { UnnestedAreaSingleSelect } from '../../../reactcomponents/Dropdown'; // Assuming Area selection is a common pattern
import { TXT_NO_SELECTION } from '../../../common/v5/receiptGreetingConstants';

// Import the mock Lazada API service
import * as LazadaApiService from '../../../services/lazada_api_service';
import { lazadaApiService } from '../../../services/lazada_api_service';

// Helper texts - adapt from GoogleReviewForm or create new ones for Lazada
const helperForLazadaName = <div>
  <div>{I("This is the name that will be displayed in the list of Lazada accounts. This name is only used to help you identify this connection.")}</div>
</div>;

const helperForLazadaArea = <div>
  <div>{I("An area must be chosen because the Lazada connection needs to be associated with an area.")}</div>
</div>;

const helperForLazadaEnable = <div>
  <div>{I("If enabled, the system will fetch data from Lazada.")}</div>
  <div>{I("If not enabled, the system will not interact with this Lazada connection.")}</div>
</div>;

const helperForLazadaSellerId = <div>
  <div>{I("Your Lazada Seller ID will be automatically populated when you connect to Lazada. This is the unique identifier for your seller account.")}</div>
</div>;

const helperForLazadaRegion = <div>
  <div>{I("Select the Lazada region for your seller account. This determines which Lazada marketplace you're connecting to.")}</div>
</div>;

const helperForLazadaConnect = <div>
  <div>{I("Click to connect to Lazada using the provided credentials.")}</div>
</div>;

// Region options for Lazada
const LAZADA_REGIONS = [
  { value: 'SG', label: I('Singapore') },
  { value: 'MY', label: I('Malaysia') },
  { value: 'VN', label: I('Vietnam') },
  { value: 'TH', label: I('Thailand') },
  { value: 'PH', label: I('Philippines') },
  { value: 'ID', label: I('Indonesia') },
];

export default class LazadaAccountForm extends PureComponent {
  constructor(props) {
    super(props);
    /**
     * Parent-provided props (mimicking GoogleReviewAccountForm integration pattern):
     * - input: Object containing the data for the account being edited/created.
     * - areaList: Array of areas for the Area dropdown.
     * - onHandleTextInputChange: Function to call when a text input changes (for controlled components by parent).
     * - onChangeAdminInput: Function for other generic input changes propagated to parent.
     * - onSave: Function to call when the form is submitted and valid (handles actual API call to save data).
     * - baseButtons: React element containing Save/Cancel buttons, provided by parent.
     * - hidden: Boolean to control form visibility.
     * - view: String identifier for the current view/account type (e.g., 'lazada').
     */
    this.state = {
      // Initialize state from props.input if provided (for editing existing or parent-controlled new)
      id: props.input?.id || 0,
      name: props.input?.name || '',
      selectedArea: props.input?.selectedArea || '0',
      enable: props.input?.enable || false,
      accountID: props.input?.accountID || '',
      region: props.input?.region || 'MY',
      accessToken: props.input?.accessToken || null, 
      
      // Internal UI state
      connectionStatus: props.input?.accessToken ? I("Connected") : I("Not Connected"),
      isConnecting: false,
      // accessToken is also managed here after local connection attempt

      reqInputs: {
        name: { touched: false },
        selectedArea: { touched: false },
        region: { touched: false },
      },
    };
  }

  componentDidUpdate(prevProps) {
    // If parent passes new `input` props (e.g., user selects a different account to edit),
    // update the component's state to reflect the new data.
    if (this.props.input !== prevProps.input && this.props.input) {
      this.setState({
        id: this.props.input.id || 0,
        name: this.props.input.name || '',
        selectedArea: this.props.input.selectedArea || '0',
        enable: this.props.input.enable || false,
        accountID: this.props.input.accountID || '',
        region: this.props.input.region || 'MY',
        accessToken: this.props.input.accessToken || null,
        connectionStatus: this.props.input.accessToken ? I("Connected ") : I("Not Connected"),
        // Reset touched state for reqInputs if needed, or re-evaluate based on new input
      });
    }
  }

  componentDidMount() {
    // Check if we're in the callback flow
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    if (code) {
      this.handleCallback(code);
    }

    // Add event listener for popup window authorization callback
    $(document.body).on('lazada.authorization.accounts', this.handleAuthorizationCallback);
  }

  componentWillUnmount() {
    // Remove event listener when component unmounts
    $(document.body).off('lazada.authorization.accounts', this.handleAuthorizationCallback);
  }

  // Handles the authorization callback from the popup window
  handleAuthorizationCallback = (event, data) => {
    if (data.error) {
      this.setState({
        isConnecting: false,
        connectionStatus: I("Connection Failed: ") + data.error,
        error: data.error
      });
      return;
    }

    if (data.access_token) {
      this.handleCallback(data.access_token);
    }
  };

  // Handles text input changes. If onHandleTextInputChange prop is provided by parent,
  // it's used to allow parent to manage state (controlled component pattern).
  // Otherwise, falls back to internal component state.
  handleGenericInputChange = (eOrValue, fieldNamePassed) => {
    let name, value;
    if (eOrValue && typeof eOrValue.target === 'object') { // Standard event object
        name = eOrValue.target.name;
        value = eOrValue.target.type === 'checkbox' ? eOrValue.target.checked : eOrValue.target.value;
    } else { // Direct value passed
        name = fieldNamePassed;
        value = eOrValue;
    }

    // Debug logging for region selection
    if (fieldNamePassed === 'region') {
      console.log('[DEBUG] Region selection:', { eOrValue, fieldNamePassed, name, value });
    }

    if (this.props.onHandleTextInputChange) { 
      // Create a mock event object if only value and fieldName were passed, 
      // if the parent handler expects an event object.
      const mockEvent = { target: { name, value } }; 
      this.props.onHandleTextInputChange(mockEvent, this.props.view);
    } else if (this.props.onChangeAdminInput && fieldNamePassed) {
      // onChangeAdminInput is often used for non-standard inputs or direct state updates in parent
      this.props.onChangeAdminInput(fieldNamePassed, eOrValue, this.props.view);
    } else {
      this.setState({ [name]: value });
    }

    if (this.state.reqInputs[name]) {
      this.setState(prevState => ({
        reqInputs: update(prevState.reqInputs, {
          [name]: { touched: { $set: true } },
        }),
      }));
    }
  };

  handleSelectArea = (val) => {
    const fieldName = "selectedArea";
    console.log('[DEBUG] Area selected:', val);
    
    if (this.props.onChangeAdminInput) {
        this.props.onChangeAdminInput(fieldName, val, this.props.view);
    } else {
        this.setState({ [fieldName]: val });
    }
    this.setState(prevState => ({
      reqInputs: update(prevState.reqInputs, {
        [fieldName]: { touched: { $set: true } },
      }),
    }));
  };

  handleSelectRegion = (val) => {
    console.log('[DEBUG] Region selected:', val);
    const fieldName = "region";
    
    if (this.props.onChangeAdminInput) {
        this.props.onChangeAdminInput(fieldName, val, this.props.view);
    } else {
        this.setState({ [fieldName]: val });
    }
    this.setState(prevState => ({
      reqInputs: update(prevState.reqInputs, {
        [fieldName]: { touched: { $set: true } },
      }),
    }));
  };

  handleToggleEnable = (value, fieldName) => { // fieldName will be 'enable'
    if (this.props.onChangeAdminInput) {
        this.props.onChangeAdminInput(fieldName, value, this.props.view);
    } else {
        this.setState({ [fieldName]: value });
    }
  };

  handleConnect = async () => {
    try {
      this.setState({ 
        isConnecting: true, 
        connectionStatus: I("Connecting..."),
        error: null,
        success: null
      });

      // Get the authorization URL
      const redirectURI = window.location.origin + '/media/lazada/handletoken';
      const authUrl = await lazadaApiService.getAuthUrl(redirectURI);
      
      // Open popup window for authorization
      const popup = window.open(
        authUrl,
        'lazada_auth',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      );

      // Check if popup was blocked
      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        this.setState({
          isConnecting: false,
          connectionStatus: I("Connection Failed: Popup blocked"),
          error: I("Please allow popups for this site to connect to Lazada")
        });
        return;
      }

      // The popup will handle the authorization and trigger the event
      // The event listener will handle the callback
    } catch (error) {
      this.setState({
        isConnecting: false,
        connectionStatus: I("Connection Failed: ") + (error.message || 'Failed to connect to Lazada'),
        error: error.message || 'Failed to connect to Lazada'
      });
    }
  };

  handleTestConnection = async () => {
    try {
      const currentData = this.props.input || this.state;
      const accessToken = this.state.accessToken || currentData.accessToken;
      
      if (!accessToken) {
        alert(I("Please connect to Lazada first."));
        return;
      }

      this.setState({ 
        isConnecting: true, 
        connectionStatus: I("Testing connection..."),
        error: null,
        success: null
      });

      const success = await lazadaApiService.testConnection(accessToken);
      
      this.setState({
        isConnecting: false,
        connectionStatus: success ? I("Connection Test Successful") : I("Connection Test Failed"),
        success: success ? I("Successfully connected to Lazada API") : null,
        error: success ? null : I("Failed to connect to Lazada API")
      });
    } catch (error) {
      this.setState({
        isConnecting: false,
        connectionStatus: I("Connection Test Failed: ") + (error.message || 'Failed to test connection'),
        error: error.message || 'Failed to test connection'
      });
    }
  };

  handleCallback = async (accessToken) => {
    try {
      this.setState({ 
        isConnecting: true, 
        connectionStatus: I("Processing token..."),
        error: null,
        success: null
      });

      // Get the token info (including account ID)
      const tokenInfo = await lazadaApiService.getTokenInfo(accessToken);

      // Update the form with the account ID and access token
      this.setState({
        accountID: tokenInfo.accountID,
        accessToken: accessToken,
        isConnecting: false,
        connectionStatus: I("Connected - Token: ") + accessToken.substring(0, 20) + '...',
        success: 'Successfully connected to Lazada'
      });

      // If parent manages state, inform it of the new access token and account ID
      if (this.props.onChangeAdminInput) {
        this.props.onChangeAdminInput("accessToken", accessToken, this.props.view);
        this.props.onChangeAdminInput("accountID", tokenInfo.accountID, this.props.view);
      }

      // Remove the code from URL to prevent re-processing (if it exists)
      if (window.location.search.includes('code=')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      this.setState({
        isConnecting: false,
        connectionStatus: I("Connection Failed: ") + (error.message || 'Failed to complete Lazada connection'),
        error: error.message || 'Failed to complete Lazada connection'
      });
    }
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const currentData = this.props.input || this.state;
    const currentAccessToken = this.state.accessToken || currentData.accessToken;
    let valid = true;

    // Reset touched state for all required fields for validation display
    this.setState(prevState => ({
      reqInputs: update(prevState.reqInputs, {
        selectedArea: { touched: { $set: true } },
        name: { touched: { $set: true } },
        region: { touched: { $set: true } },
      }),
    }));

    // Validate area selection
    if (!currentData.selectedArea || currentData.selectedArea === "0" || currentData.selectedArea === 0) {
      alert(I("Please select an area."));
      valid = false;
    } else {
      // Check if selected area exists in the area list
      const selectedAreaId = parseInt(currentData.selectedArea, 10);
      const areaExists = this.props.areaList?.some(area => {
        // Handle nested structure if present
        if (area.Areas) {
          return area.Areas.some(nestedArea => nestedArea.Id === selectedAreaId);
        }
        return area.Id === selectedAreaId;
      });
      
      if (!areaExists) {
        alert(I("The selected area is not valid. Please select a different area."));
        console.error('[DEBUG] Selected area not found in list:', { 
          selectedAreaId, 
          areaList: this.props.areaList 
        });
        valid = false;
      }
    }

    // Validate name
    if (!currentData.name || currentData.name.trim() === "") {
      if (valid) alert(I("Please enter a connection name."));
      valid = false;
    }

    // Validate account ID - now automatically populated, but still required
    if (!currentData.accountID || currentData.accountID.trim() === "") {
      if (valid) alert(I("Please connect to Lazada to obtain your Account ID."));
      valid = false;
    }

    // Validate region
    if (!currentData.region || currentData.region.trim() === "") {
      if (valid) alert(I("Please select a Lazada region."));
      valid = false;
    }

    // Validate connection (either existing token or need to connect)
    if (!currentAccessToken) { 
        alert(I("Please connect to Lazada first."));
        valid = false;
    }

    if (valid) {
      if (this.props.onSave) {
        // Data to be saved is taken from currentData (which is props.input or this.state)
        // and includes the accessToken obtained during connection.
        const saveData = {
            id: currentData.id,
            name: currentData.name,
            selectedArea: currentData.selectedArea,
            enable: currentData.enable,
            accountID: currentData.accountID,
            region: currentData.region,
            accessToken: currentAccessToken, // Use the most up-to-date token
        };
        // The onSave prop is a function provided by the parent admin component.
        // That parent function is responsible for making the actual API call to the backend.
        this.props.onSave(saveData);
      } else {
        console.warn("LazadaAccountForm: onSave prop not provided. Form is valid but data cannot be saved.");
        alert(I("Form is valid but no onSave handler is configured."));
      }
    } else {
      alert(FORM_SUBMIT_EMPTY_WARNING || I("Please fill all mandatory fields and connect to Lazada."));
    }
  };

  render() {
    const {
      areaList, // Prop from parent: list of areas for dropdown
      input,    // Prop from parent: current account data (if editing or parent-controlled)
      hidden,   // Prop from parent: to show/hide form
      baseButtons, // Prop from parent: Save/Cancel buttons UI
    } = this.props;

    // Debug logging for area data structure
    console.log('[DEBUG] Area list structure:', areaList);
    console.log('[DEBUG] Area list sample:', areaList?.[0]);
    console.log('[DEBUG] Area list has nested Areas:', areaList?.[0]?.Areas ? 'YES' : 'NO');
    if (areaList?.[0]?.Areas) {
      console.log('[DEBUG] Nested area sample:', areaList[0].Areas[0]);
    }

    // Determine data source: parent-controlled `input` prop or internal `state`.
    const currentData = input || this.state;
    const { selectedArea, name, enable, accountID, region } = currentData;
    // Ensure selectedArea is an integer for the dropdown component
    const currentSelectedArea = parseInt(selectedArea, 10) || 0; 

    const st = this.state; // For internal UI state like isConnecting, connectionStatus
    const fieldState = st.reqInputs; // For validation UI (touched status)
    const warnTxt = INPUT_EMPTY_WARNING;

    // Debug logging for region state
    console.log('[DEBUG] Render - Region state:', { region, currentData: currentData.region });
    console.log('[DEBUG] Render - LAZADA_REGIONS:', LAZADA_REGIONS);
    console.log('[DEBUG] Render - Selected area:', { selectedArea, currentSelectedArea });

    return (
      <form onSubmit={this.handleSubmit} className="admin-one-form edit-admin-form" hidden={hidden}>
        <CustomInputRow
          id={"area"}
          name={"area"}
          label={I('Area')}
          className={"admin-dropdown-select"}
          mandatory={true}
          helper={helperForLazadaArea}
        >
          <UnnestedAreaSingleSelect
            data={areaList || []} 
            invalidSelectedText={TXT_NO_SELECTION}
            onSelect={this.handleSelectArea} // Uses internal handler which then calls parent prop if available
            selected={currentSelectedArea}
            nested={{key: 'Areas'}}
            idFields={{ id: "Id", name: "Name" }}
          />
           {(!currentSelectedArea || currentSelectedArea === 0) && fieldState.selectedArea.touched && 
             <div className="form-field-error-text">{warnTxt}</div>}
        </CustomInputRow>

        <TextInputRow
          id={"name"}
          name={"name"}
          className={"admin-text-input"}
          label={I("Connection Name")}
          value={name || ""} 
          onChange={(e) => this.handleGenericInputChange(e)} 
          mandatory={true}
          warning={(!name && fieldState.name.touched) && warnTxt}
          onBlur={(e) => this.handleGenericInputChange(e)} 
          helperTxt={helperForLazadaName}
        />

        <ToggleSwitch
          id={"enable"}
          name={"enable"}
          label={I("Enabled")}
          checked={!!enable}
          onClick={(val) => this.handleToggleEnable(val, "enable")} 
          listWrap={false}
          className={"admin-text-input"}
          helperTxt={helperForLazadaEnable}
        />
        
        <ReadOnlyTextField
          id={"accountID"}
          name={"accountID"}
          className={"admin-text-input"}
          label={I("Lazada Seller ID")}
          value={accountID || I("Connect to Lazada to auto-populate")}
          helperTxt={helperForLazadaSellerId}
        />

        <CustomInputRow
          id={"region"}
          name={"region"}
          label={I("Lazada Region")}
          className={"admin-dropdown-select"}
          mandatory={true}
          helper={helperForLazadaRegion}
        >
          <UnnestedAreaSingleSelect
            data={LAZADA_REGIONS}
            invalidSelectedText={TXT_NO_SELECTION}
            onSelect={this.handleSelectRegion}
            selected={region}
            idFields={{ id: "value", name: "label" }}
          />
           {(!region || region === '') && fieldState.region.touched && 
             <div className="form-field-error-text">{warnTxt}</div>}
        </CustomInputRow>

        <CustomInputRow
          id={"lazadaConnection"}
          name={"lazadaConnection"}
          label={I("Lazada Connection")}
          className={"admin-auth-button"}
          helper={helperForLazadaConnect}
          mandatory={!st.accessToken && !currentData.accessToken}
        >
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            className="btn-outline blue btn-icon-text"
            outline
            color="primary"
            onClick={this.handleConnect}
            disabled={st.isConnecting}
              title={I("Connect to Lazada")}
          >
            {st.isConnecting ? <Spinner size="sm" /> : <i className={"icon-link"} />}
            <span className="label">{st.isConnecting ? I("Connecting...") : I("Connect to Lazada")}</span>
          </Button>
            
            {(st.accessToken || currentData.accessToken) && (
              <Button
                className="btn-outline green btn-icon-text"
                outline
                color="success"
                onClick={this.handleTestConnection}
                disabled={st.isConnecting}
                title={I("Test Connection")}
              >
                {st.isConnecting ? <Spinner size="sm" /> : <i className={"icon-check"} />}
                <span className="label">{st.isConnecting ? I("Testing...") : I("Test Connection")}</span>
              </Button>
            )}
          </div>
          
          <div style={{ marginTop: '5px', fontSize: '0.9em', color: st.accessToken || currentData.accessToken ? 'green' : 'orange' }}>
            {st.connectionStatus}
          </div>
          
          {st.success && (
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: 'green' }}>
              {st.success}
            </div>
          )}
          
          {st.error && (
            <div style={{ marginTop: '5px', fontSize: '0.9em', color: 'red' }}>
              {st.error}
            </div>
          )}
        </CustomInputRow>
 
        {(st.accessToken || currentData.accessToken) && (
          <ReadOnlyTextField
            id={"authenticationStatus"}
            name={"authenticationStatus"}
            className={"admin-text-input"}
            label={I("Authentication Status")}
            value={I("Connected to Lazada")}
          />
        )}

        {/* baseButtons prop from parent will render Save/Cancel etc. */}
        {baseButtons} 
      </form>
    );
  }
} 